import { Component, type ErrorInfo, type ReactNode } from "react";
import i18next from "@/i18n";
import logger from "@/lib/logging/logger";

type Props = {
	children: ReactNode;
	fallback?: ReactNode;
};

type State = {
	hasError: boolean;
};

/**
 * Last line of defence for a render that throws.
 *
 * Without one, a render-time failure unmounts the whole tree and leaves a blank page with
 * nothing in the logs, which is how an unreadable price surfaced to users. Copy comes from
 * `i18next.t` rather than the hook because a class component cannot use hooks.
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		logger.error("INFO", "Unhandled render error", { error, componentStack: info.componentStack });
	}

	render() {
		if (!this.state.hasError) return this.props.children;

		return (
			this.props.fallback ?? (
				<div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-4">
					<h1 className="subheading-2 mb-4">{i18next.t("modal.Oops! Something went wrong")}</h1>
					<p className="body-reg text-dark-grey mb-8">
						{i18next.t("modal.Please try again as an unexpected error occurred!")}
					</p>
					<button
						className="px-6 py-2 bg-dark text-light rounded"
						onClick={() => window.location.reload()}
					>
						{i18next.t("buttons.Try again")}
					</button>
				</div>
			)
		);
	}
}
